import { Component, OnInit } from '@angular/core';
import {
  CONTRACTOR_LEAVES_COLUMNS,
  FRAMEWORK_COMPONENTS,
  LEAVE_TABLE_CONFIG,
} from '../contractor-flow-table-configuration';
import { TableConfig } from '@shared/components/table';
import { ContractorFlowService } from '../contractor-flow.service';
import { ContractorLeave, LeaveApplyRequest } from '@interfaces/contractor-flow.model';
import { AuthService } from '@core/auth.service';
import { ContractorTimesheetComponent } from '../contractor-timesheet/contractor-timesheet.component';
import { ToastrService } from 'ngx-toastr';
import { VendorEmployeeEnum } from '@app/shared/constants/global.enum';
import { VendorService } from '@app/shared/services/vendor.service';

@Component({
  selector: 'app-contractor-leave',
  templateUrl: './contractor-leave.component.html',
  styleUrl: './contractor-leave.component.scss',
})
export class ContractorLeaveComponent implements OnInit {
  public readonly LEAVES_COLUMNS = CONTRACTOR_LEAVES_COLUMNS;
  leavesData: ContractorLeave[] = [];
  tableConfig: TableConfig = LEAVE_TABLE_CONFIG;
  maxTableHeight: string = '300px';
  gridOptions: any = {
    context: {
      componentParent: this,
    },
  };
  frameworkComponents = FRAMEWORK_COMPONENTS;
  showApplyLeaveModal: boolean = false;
  VendorEmployeeEnum = VendorEmployeeEnum;
  quarterlyLimit: number | null = null;
  static instance: ContractorLeaveComponent;

  constructor(
    private authService: AuthService,
    private contractorFlowService: ContractorFlowService,
    private toasterService: ToastrService,
    private vendorService: VendorService,
  ) {
    ContractorLeaveComponent.instance = this;
  }

  ngOnInit(): void {
    this.loadLeavesData();
    this.loadQuarterlyLimit(); 
  }

  private loadLeavesData(): void {
    const employeeId = this.authService.userProfile?.employeeId;
    if (employeeId) {
      this.contractorFlowService
        .getContractorLeaves(employeeId)
        .subscribe((data) => {
          this.leavesData = Array.isArray(data) ? data : data?.data || [];
        });
    }
  }

  public reloadLeavesData(): void {
    this.loadLeavesData();
  }

  onDataSaved(): void {
    this.loadLeavesData();
    if (ContractorTimesheetComponent.instance) {
      ContractorTimesheetComponent.instance.reloadTimesheetData();
    }
  }

  applyLeave() {
    this.showApplyLeaveModal = true;
  }

  checkTimesheetStatusForLeave(leaveStartDate: string): Promise<string> {
    return new Promise<string>((resolve) => {
      const employeeId: any = this.authService.userProfile?.employeeId;

      this.contractorFlowService.getContractorTimesheetApi(employeeId.toString()).subscribe({
      next: (response: any) => {
        const timesheetData = response?.data ?? [];
        const leaveDate = new Date(leaveStartDate);
        const leaveMonth = leaveDate.getMonth() + 1;
        const leaveYear = leaveDate.getFullYear();

        const monthTimesheet = timesheetData.find(
        (item: any) =>
          Number(item?.monthNumber) === leaveMonth &&
          Number(item?.year) === leaveYear
        );

        resolve(monthTimesheet?.status?.toLowerCase() ?? '');
      },
      error: () => resolve(''),
      });
    });
  }

  onCancelLeave(leaveData: any): void {
    const employeeId = this.authService.userProfile?.employeeId;
    const applyLeaveData: LeaveApplyRequest = {
      employeeId: employeeId ? employeeId.toString() : '',
      comments: leaveData.comments,
      attachmentUrl: leaveData.fileAttachmentUrl,
      noOfLeaves: leaveData.noOfLeaves,
      status: 'Cancelled',
      leaveEntries: [
        {
          fromDate: leaveData.leaveStartDate,
          toDate: leaveData.leaveEndDate,
          leaveType: leaveData.leaveType,
        },
      ],
    };

    this.contractorFlowService.saveApplyLeave(applyLeaveData).subscribe({
      next: (res: any) => {
        this.toasterService.success(this.VendorEmployeeEnum.cancelLeave);

        // After a successful cancel, add the cancelled working days back into billableDates
        this.addCancelledDatesToBilling(
          employeeId?.toString() ?? '',
          leaveData.leaveStartDate,
          leaveData.leaveEndDate,
        );
      },
      error: () => {
        this.toasterService.error(this.VendorEmployeeEnum.errorLeave);
      },
    });
  }

  /**
   * After a leave is cancelled, fetch the current billableDates for that month,
   * add back every cancelled working day (excluding weekends and public holidays),
   * and call recalculateEmployeeTimesheet so billing stays accurate.
   */
  private addCancelledDatesToBilling(
    employeeId: string,
    fromDate: string,   // yyyy-MM-dd
    toDate: string,     // yyyy-MM-dd
  ): void {
    this.contractorFlowService.getContractorTimesheetApi(employeeId).subscribe({
      next: (response: any) => {
        const timesheetData: any[] = response?.data ?? [];

        // Determine which month(s) the leave spans — use fromDate as the anchor
        const start = new Date(fromDate);
        const end = new Date(toDate);
        const leaveMonth = start.getMonth() + 1;
        const leaveYear = start.getFullYear();

        const monthEntry = timesheetData.find(
          (item: any) =>
            Number(item.monthNumber) === leaveMonth &&
            Number(item.year) === leaveYear,
        );

        // Existing billableDates for this month (format: dd-MM-yyyy)
        const existingBillable: string[] = monthEntry?.billableDates ?? [];

        // Public holidays for this month (format: yyyy-MM-dd)
        const publicHolidays: Set<string> = new Set(
          monthEntry?.publicHolidayDates ?? [],
        );

        // Expand every working day in the cancelled range
        const toAdd: string[] = [];
        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
          const day = d.getDay();
          if (day === 0 || day === 6) continue; // skip weekends

          const isoStr = this.toIsoDateStr(d);
          if (publicHolidays.has(isoStr)) continue; // skip public holidays

          // Convert to dd-MM-yyyy for the billing API
          const ddMMyyyy = this.toDdMmYyyy(d);
          toAdd.push(ddMMyyyy);
        }

        if (toAdd.length === 0 || existingBillable.length === 0) {
          // No working days to add back, or no existing billable dates for this month — just reload the UI
          this.reloadAll();
          return;
        }

        // Merge with existing, deduplicate
        const merged = Array.from(new Set([...existingBillable, ...toAdd]));

        this.vendorService.recalculateEmployeeTimesheet(employeeId, merged).subscribe({
          next: () => this.reloadAll(),
          error: () => {
            // Billing update failed — still reload UI so leave cancel is visible
            this.reloadAll();
          },
        });
      },
      error: () => {
        // Timesheet fetch failed — still reload UI
        this.reloadAll();
      },
    });
  }

  /** Reload both the leaves table and the timesheet table. */
  private reloadAll(): void {
    this.loadLeavesData();
    if (ContractorTimesheetComponent.instance) {
      ContractorTimesheetComponent.instance.reloadTimesheetData();
    }
  }

  /** Returns yyyy-MM-dd string for a Date (local time). */
  private toIsoDateStr(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  /** Returns dd-MM-yyyy string — the format the billing API expects. */
  private toDdMmYyyy(d: Date): string {
    const day = String(d.getDate()).padStart(2, '0');
    const m = String(d.getMonth() + 1).padStart(2, '0');
    return `${day}-${m}-${d.getFullYear()}`;
  }

  private loadQuarterlyLimit(): void {
  const employeeId = this.authService.userProfile?.employeeId;
  if (!employeeId) return;
  this.contractorFlowService.getContractorTimesheetApi(employeeId.toString()).subscribe({
    next: (response: any) => {
      const today = new Date();
      const currentMonth = today.getMonth() + 1;
      const currentYear = today.getFullYear();
      const currentMonthData = (response?.data ?? []).find(
        (item: any) =>
          Number(item.monthNumber) === currentMonth &&
          Number(item.year) === currentYear
      );

      this.quarterlyLimit = currentMonthData?.quarterlyLimit ?? null;
    },
    error: () => {
      this.quarterlyLimit = null;
    },
  });
}

  closeApplyLeaveModal() {
    this.showApplyLeaveModal = false;
  }
}